import React, { useContext, useState } from 'react';
import { ConfirmationModal } from '@stormwater-software/lib-ui';
import NotificationContext from '../../../contexts/NotificationsContext';
import MFAVerificationModal from '../MFAVerificationModal';
import useNotificationNavigate, {
  INotification,
} from '../../../hooks/useNotificationNavigate';
import NotificationPopper from './NotificationPopper';
import NotificationsModal from './NotificationsModal';

/**
 * Passed to `onClosePopper` so the host can tell an expand or a clear apart from
 * a plain dismissal, and skip marking everything read.
 */
export const CLOSED_BY_ACTION = 'notification-action';

export interface INotificationsCenterProps {
  showPopper: boolean;
  coordinates: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  onClosePopper: (data?: unknown) => void;
}

/**
 * Everything behind the notification bell (SW2-1651).
 *
 * Rendered by the nav bars unconditionally, not inside the popper: the popper
 * closes on mouse leave, so the expanded modal, the clear-all confirmation and
 * the MFA prompt a cross-tenant redirect may raise all have to live out here to
 * survive.
 */
const NotificationsCenter: React.FC<INotificationsCenterProps> = ({
  showPopper,
  coordinates,
  onClosePopper,
}) => {
  const notificationContext = useContext(NotificationContext);
  const { navigate, mfaModal } = useNotificationNavigate();

  const [showModal, setShowModal] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const handleSelect = (notification: INotification) => {
    navigate(notification);
    onClosePopper(notification.id);
  };

  const handleExpand = () => {
    setShowModal(true);
    onClosePopper(CLOSED_BY_ACTION);
  };

  const handleClearAllRequest = () => {
    setConfirmingClear(true);
    onClosePopper(CLOSED_BY_ACTION);
  };

  const handleClearAll = () => {
    setConfirmingClear(false);

    notificationContext.clearAllNotifications().catch(() => {
      /** The context has already refetched to resync; nothing to add here. */
    });
  };

  return (
    <>
      {showPopper && (
        <NotificationPopper
          coordinates={coordinates}
          handleClose={onClosePopper}
          onSelect={handleSelect}
          onExpand={handleExpand}
          onClearAll={handleClearAllRequest}
        />
      )}

      <NotificationsModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        onSelect={handleSelect}
      />

      {confirmingClear && (
        <ConfirmationModal
          show
          title='Clear all notifications?'
          subTitle='This removes every notification from your list, across all tenants. It cannot be undone.'
          buttonText='Clear all'
          handleClose={() => setConfirmingClear(false)}
          handleConfirm={handleClearAll}
        />
      )}

      {mfaModal.hasData && (
        <MFAVerificationModal
          show={mfaModal.show}
          handleClose={mfaModal.handleClose}
          onVerificationSuccess={mfaModal.onVerificationSuccess}
          email={mfaModal.email}
          sendVerificationCode={mfaModal.sendVerificationCode}
          verifyCode={mfaModal.verifyCode}
        />
      )}
    </>
  );
};

export default NotificationsCenter;
